import React, { useRef, useEffect } from "react";
import { FaFileAlt, FaMobileAlt, FaPlug, FaChartBar, FaCogs, FaTools } from "react-icons/fa";
import { Link } from "react-router-dom";
import bgmitraicon from "../img/home-icon.jpg";
import { Helmet } from "react-helmet-async";

const products = [
  {
    title: "BachatGat Mitra - बचत गट मित्र",
    icon: (
      <img
        src={bgmitraicon}
        alt="BachatGat Mitra - बचत गट मित्र"
        style={{
          height: 48,
          width: 48,
          borderRadius: 8,
          border: "2px solid #009688",
          background: "#e0f2f1",
          padding: 2,
        }}
      />
    ),
    desc: "BachatGat Mitra (बचत गट मित्र) is a mobile app for Self Help Groups (SHGs), empowering members with digital tools and support.",
    link: "/bg-mitra",
  },
];

const services = [
  {
    title: "Custom Software Solutions",
    icon: <FaCogs size={48} style={{ color: "#6d4c41" }} />,
    desc: "Bespoke software solutions to solve unique business challenges.",
  },
  {
    title: "Mobile & Web Application Development",
    icon: <FaMobileAlt size={48} style={{ color: "#1976d2" }} />,
    desc: "Custom mobile and web apps tailored to your business needs.",
  },
  {
    title: "API Integration & Consulting",
    icon: <FaPlug size={48} style={{ color: "#ff9800" }} />,
    desc: "Seamless integration of APIs and expert consulting for digital transformation.",
  },
  {
    title: "Data Analytics & Reporting",
    icon: <FaChartBar size={48} style={{ color: "#43a047" }} />,
    desc: "Transform data into actionable insights with analytics and reporting.",
  },
  {
    title: "Technical Support & Maintenance",
    icon: <FaTools size={48} style={{ color: "#e53935" }} />,
    desc: "Reliable technical support and maintenance for your digital assets.",
  },
  {
    title: "Digital Document Management",
    icon: <FaFileAlt size={48} style={{ color: "#009688" }} />,
    desc: "Secure and efficient management of digital documents for your organization.",
  },
];

const HomePage = () => {
  const scrollRef = useRef(null);

  // Auto-scroll logic for services section
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    let scrollAmount = 0;
    const cardWidth = 280 + 24; // card width + gap
    const interval = setInterval(() => {
      if (container.scrollLeft + container.offsetWidth >= container.scrollWidth) {
        // If at end, scroll back to start
        container.scrollTo({ left: 0, behavior: "smooth" });
        scrollAmount = 0;
      } else {
        scrollAmount += cardWidth;
        container.scrollTo({ left: scrollAmount, behavior: "smooth" });
      }
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div>
      {/* Hero Section */}
      <Helmet>
        <title>Vakralabs – Custom Software & API Integration Experts</title>
        <meta
          name="description"
          content="Vakralabs delivers creative, custom-built software solutions, mobile/web apps, and API integrations to help businesses grow."
        />
        <meta property="og:title" content="Vakralabs – Custom Software Experts" />
        <meta property="og:description" content="Simplifying complexity through tailored software, apps, and API solutions." />
        <meta property="og:image" content="/src/img/icons/vakra_logo_1024x1024.png" />
      </Helmet>
      <section className="bg-teal text-white py-5 text-center">
        <h1 className="display-4 fw-bold">Vakralabs Services</h1>
        <p className="lead" style={{ fontFamily: "cursive" }}>
          Simplifying Complexity to Deliver Creativity
        </p>
        <p>
          Custom Software, Mobile/Web Apps, API Integrations, and Digital Products for your business growth.
        </p>
        <Link to="/contact" className="btn btn-light btn-lg mt-3">
          Get a Free Consultation
        </Link>
      </section>

      {/* Services Section (Auto-scrolling) */}
      <section className="container py-5">
        <h2 className="mb-4 text-center">Our Services</h2>
        <div
          className="d-flex flex-row flex-nowrap overflow-auto pb-3"
          style={{ gap: 24, scrollBehavior: "smooth" }}
          ref={scrollRef}
        >
          {services.map((service, idx) => (
            <div
              key={idx}
              className="card shadow-sm text-center"
              style={{
                minWidth: 260,
                maxWidth: 280,
                display: "inline-block",
                marginRight: 8,
                verticalAlign: "top",
              }}
            >
              <div className="card-body d-flex flex-column align-items-center justify-content-center">
                <div className="mb-3">{service.icon}</div>
                <h5 className="card-title">{service.title}</h5>
                <p className="card-text" style={{ fontSize: "0.98em" }}>{service.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Products Section (Centered) */}
      <section className="container py-5">
        <h2 className="mb-4 text-center">Our Products</h2>
        <div className="d-flex justify-content-center">
          <div className="d-flex flex-row flex-nowrap pb-3" style={{ gap: 24 }}>
            {products.map((product, idx) => (
              <div
                key={idx}
                className="card shadow-sm text-center"
                style={{
                  minWidth: 260,
                  maxWidth: 280,
                  display: "inline-block",
                  marginRight: 8,
                  verticalAlign: "top",
                }}
              >
                <div className="card-body d-flex flex-column align-items-center justify-content-center">
                  <div className="mb-3">{product.icon}</div>
                  <h5 className="card-title">{product.title}</h5>
                  <p className="card-text" style={{ fontSize: "0.98em" }}>{product.desc}</p>
                  <Link to={product.link} className="btn btn-teal mt-2">
                    Learn More
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="bg-blue-50 py-5">
        <h2 className="text-center mb-4">Why Choose Vakralabs?</h2>
        <ul className="max-w-4xl mx-auto grid sm:grid-cols-2 gap-4 text-lg" style={{ listStyle: "none" }}>
          <li>✔️ Agile & Transparent Process</li>
          <li>✔️ Experienced Development Team</li>
          <li>✔️ Scalable Architecture & Clean Code</li>
          <li>✔️ On-time Delivery & Post-launch Support</li>
        </ul>
      </section>

      {/* Call to Action */}
      <section className="py-5 text-center">
        <h2 className="text-2xl font-bold mb-4">Ready to simplify your software challenges?</h2>
        <Link to="/contact" className="btn btn-teal btn-lg">
          Contact Us
        </Link>
      </section>
    </div>
  );
};

export default HomePage;